/**
 * LeadPreviewPanel — Enhanced right-side Sheet for the work queue.
 *
 * Shows "Why this business, why now" with disposition actions:
 * Accept (start deal), Snooze, Discard (with reason).
 *
 * Adapted from LeadDetailPanel.jsx but focused on queue disposition
 * rather than kanban stage management.
 */
import React, { useState } from 'react';
import {
  MapPin, Phone, Globe, Star, Camera, Zap, Loader2,
  ArrowRight, Clock, Ban, Sparkles, LayoutGrid, DollarSign,
  Target, AlertTriangle, TrendingUp,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter,
} from '@/components/ui/sheet';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { isEnriched, isEnriching, hasMediaPlan, formatRelativeTime, getConfidence } from '@/components/leads/mockData';
import {
  DISCARD_REASONS, SNOOZE_OPTIONS, getWorkspaceStatus, estimateDealValue,
  computeDealScore, getNextBestAction, getStaleDays, getFunnelStageIndex, PRIORITY_CONFIG,
} from '@/components/deal/mockDealData';

const CONFIDENCE_COLORS = {
  high: 'bg-emerald-100 text-emerald-700',
  medium: 'bg-amber-100 text-amber-700',
  low: 'bg-slate-100 text-slate-600',
};

export default function LeadPreviewPanel({
  lead, open, onClose, onAccept, onSnooze, onDiscard,
  businesses, creatives, proposals, onResume,
}) {
  const [showDiscardForm, setShowDiscardForm] = useState(false);
  const [discardReason, setDiscardReason] = useState('');

  if (!lead) return null;

  const enriched = isEnriched(lead);
  const enriching = isEnriching(lead);
  const confidence = getConfidence(lead);
  const isBackendLead = !!lead.fromBackend;
  const wsStatus = isBackendLead ? lead.queueStatusLabel : getWorkspaceStatus(lead, businesses, creatives, proposals);
  const dealValue = isBackendLead ? (lead.queueStatusLabel || 'In progress') : estimateDealValue(lead);
  const hasWorkspace = isBackendLead
    ? !!lead.workspaceId
    : !!(lead.businessProfileId && businesses?.[lead.businessProfileId]);

  // Phase 12: intelligence
  const dealScore = isBackendLead
    ? {
      score: lead.needsAttention ? 90 : lead.stale ? 65 : 40,
      priorityLabel: lead.needsAttention ? 'Act soon' : lead.stale ? 'Needs follow-up' : 'Monitor',
      priorityClassName: lead.needsAttention
        ? 'bg-coral/5 text-coral border-coral/20'
        : lead.stale
          ? 'bg-amber-50 text-amber-700 border-amber-200'
          : 'bg-slate-100 text-slate-600 border-slate-200',
    }
    : computeDealScore(lead, businesses, creatives, proposals);
  const nba = isBackendLead
    ? {
      label: lead.nextActionLabel || lead.recommendedNextStepText || 'Open workspace',
      description: lead.nextActionDescription || lead.recommendedNextStepText || 'Continue this workspace.',
    }
    : getNextBestAction(lead, businesses, creatives, proposals);
  const staleInfo = isBackendLead
    ? {
      isStale: lead.stale,
      days: lead.lastActivityAt
        ? Math.max(0, Math.round((Date.now() - new Date(lead.lastActivityAt).getTime()) / (1000 * 60 * 60 * 24)))
        : 0,
    }
    : getStaleDays(lead, businesses, creatives, proposals);
  const funnelIdx = isBackendLead
    ? (
      lead.queueStage === 'live' || lead.queueStage === 'completed_or_terminal'
        ? 3
        : ['awaiting_buyer', 'accepted_pending_billing', 'ready_to_launch'].includes(lead.queueStage)
          ? 2
          : ['creative_ready', 'offer_in_progress'].includes(lead.queueStage)
            ? 1
            : 0
    )
    : getFunnelStageIndex(lead, businesses, creatives, proposals);
  const FUNNEL_LABELS = ['Discovery', 'Creative', 'Proposal', 'Closed'];

  const handleClose = () => {
    setShowDiscardForm(false);
    setDiscardReason('');
    onClose();
  };

  const handleDiscard = () => {
    onDiscard(lead, discardReason);
    handleClose();
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && handleClose()}>
      <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col">
        {/* Header */}
        <SheetHeader className="p-6 pb-4 space-y-3 border-b border-border">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className={CONFIDENCE_COLORS[confidence]}>
              {confidence === 'high' ? 'High match' : confidence === 'medium' ? 'Good match' : 'Needs data'}
            </Badge>
            {lead.status === 'active' && hasMediaPlan(lead) && (
              <Badge variant="outline" className="bg-blue-100 text-blue-700">
                {lead.mediaPlans.length} plan{lead.mediaPlans.length > 1 ? 's' : ''}
              </Badge>
            )}
            {lead.status !== 'closed' && (
              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                <DollarSign className="h-3 w-3" />
                {dealValue}
              </Badge>
            )}
          </div>
          <div>
            <SheetTitle className="text-xl">{lead.businessName}</SheetTitle>
            <SheetDescription>{lead.businessCategory}</SheetDescription>
          </div>
        </SheetHeader>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
          {/* "Why this business, why now" */}
          <div>
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
              Why this business, why now
            </h4>
            <div className="bg-coral/5 border border-coral/15 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-1.5">
                <Zap className="h-3.5 w-3.5 text-coral" />
                <span className="text-xs font-semibold text-coral uppercase tracking-wide">Trigger Event</span>
              </div>
              <p className="text-sm font-medium text-foreground">{lead.triggerEvent}</p>
              <p className="text-xs text-muted-foreground mt-1">{formatRelativeTime(lead.triggerDate)}</p>
            </div>
          </div>

          {/* Enrichment Progress */}
          {enriching && (
            <div>
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Enrichment Status</h4>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center">
                <Loader2 className="h-6 w-6 text-amber-600 mx-auto mb-2 animate-spin" />
                <p className="text-sm font-semibold text-amber-900">Enriching business profile...</p>
                <p className="text-xs text-amber-700 mt-1">Resolving website, building profile...</p>
                <Progress value={55} className="mt-3 h-1.5 bg-amber-200 [&>[data-slot=progress-indicator]]:bg-amber-500" />
              </div>
            </div>
          )}

          {/* Business Details — enriched */}
          {enriched && (
            <div>
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Business Details</h4>
              <div className="space-y-2.5">
                <div className="flex items-center gap-2.5 text-sm">
                  <MapPin className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  <span className="text-foreground">{lead.businessAddress}</span>
                </div>
                <div className="flex items-center gap-2.5 text-sm">
                  <Phone className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  <span className="text-foreground">{lead.businessPhone}</span>
                </div>
                <div className="flex items-center gap-2.5 text-sm">
                  <Globe className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  <span className="text-foreground">{lead.businessWebsite}</span>
                </div>
                {lead.businessRating != null && (
                  <div className="flex items-center gap-4 mt-1">
                    <div className="flex items-center gap-1">
                      <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500" />
                      <span className="text-sm font-semibold text-foreground">{lead.businessRating}</span>
                      <span className="text-xs text-muted-foreground">({lead.businessReviewsCount})</span>
                    </div>
                    {lead.businessPhotos != null && (
                      <div className="flex items-center gap-1">
                        <Camera className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">{lead.businessPhotos} photos</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Pre-enrichment: minimal info */}
          {!enriched && !enriching && (
            <div>
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Business Details</h4>
              <div className="space-y-2.5">
                {lead.businessAddress && (
                  <div className="flex items-center gap-2.5 text-sm">
                    <MapPin className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <span className="text-foreground">{lead.businessAddress}</span>
                  </div>
                )}
                {lead.businessWebsite && (
                  <div className="flex items-center gap-2.5 text-sm">
                    <Globe className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <span className="text-foreground">{lead.businessWebsite}</span>
                  </div>
                )}
                <p className="text-xs text-muted-foreground italic">Start a deal to build a full business profile</p>
              </div>
            </div>
          )}

          {/* Workspace status */}
          {(hasWorkspace || wsStatus) && (
            <>
              <Separator />
              <div>
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Deal Status</h4>
                {wsStatus && (
                  <div className="flex items-center gap-2 mb-3">
                    <LayoutGrid className="h-3.5 w-3.5 text-coral" />
                    <span className="text-sm font-medium text-foreground">{wsStatus}</span>
                  </div>
                )}
                {hasWorkspace && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => onResume?.(lead)}
                  >
                    <LayoutGrid className="h-4 w-4" />
                    Open Business Workspace
                  </Button>
                )}
              </div>
            </>
          )}

          <Separator />

          {/* Deal intelligence section */}
          {lead.status !== 'closed' && (
            <>
              <div>
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                  Deal Intelligence
                </h4>

                {/* Priority score */}
                <div className="flex items-center gap-2 mb-3">
                  <Badge variant="outline" className={`text-xs ${dealScore.priorityClassName}`}>
                    {dealScore.priorityLabel}
                  </Badge>
                  <span className="text-xs text-muted-foreground">Score: {dealScore.score}/100</span>
                </div>

                {/* Next best action */}
                <div className="bg-coral/5 border border-coral/15 rounded-lg p-3 mb-3">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Target className="h-3 w-3 text-coral" />
                    <span className="text-xs font-semibold text-coral">Next step</span>
                  </div>
                  <p className="text-sm font-medium text-foreground">{nba.label}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{nba.description}</p>
                </div>

                {/* Deal timeline */}
                <div className="space-y-1.5 mb-3">
                  <div className="flex items-center gap-2 text-xs">
                    <Clock className="h-3 w-3 text-muted-foreground" />
                    <span className="text-muted-foreground">Added {Math.max(1, Math.round((Date.now() - new Date(lead.createdAt).getTime()) / (1000 * 60 * 60 * 24)))} days ago</span>
                  </div>
                  {staleInfo.isStale && (
                    <div className="flex items-center gap-2 text-xs">
                      <AlertTriangle className="h-3 w-3 text-amber-500" />
                      <span className="text-amber-600 font-medium">{staleInfo.days}d since last activity — needs attention</span>
                    </div>
                  )}
                </div>

                {/* Funnel position */}
                <div>
                  <p className="text-xs text-muted-foreground mb-1.5">
                    Stage {funnelIdx + 1} of 4: {FUNNEL_LABELS[funnelIdx]}
                  </p>
                  <div className="flex items-center gap-1.5">
                    {FUNNEL_LABELS.map((label, i) => (
                      <div
                        key={label}
                        className={`h-2 flex-1 rounded-full transition-colors ${
                          i <= funnelIdx ? 'bg-coral' : 'bg-muted'
                        }`}
                        title={label}
                      />
                    ))}
                  </div>
                </div>
              </div>

              <Separator />
            </>
          )}

          {/* Discard form */}
          {showDiscardForm && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-3">
              <h4 className="text-sm font-semibold text-red-700">Why are you discarding this lead?</h4>
              <Select value={discardReason} onValueChange={setDiscardReason}>
                <SelectTrigger className="bg-white">
                  <SelectValue placeholder="Select a reason" />
                </SelectTrigger>
                <SelectContent>
                  {DISCARD_REASONS.map((reason) => (
                    <SelectItem key={reason} value={reason}>{reason}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDiscardForm(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  disabled={!discardReason}
                  onClick={handleDiscard}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                >
                  Discard
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <SheetFooter className="border-t border-border p-4">
          {lead.status === 'closed' ? (
            <p className="text-xs text-muted-foreground text-center w-full">
              This lead is closed ({lead.closedReason === 'won' ? 'Won' : 'Lost'})
            </p>
          ) : (
            <div className="flex gap-2 w-full">
              {/* Primary: Accept / Start Deal */}
              <Button
                className="flex-1 bg-coral hover:bg-coral/90 text-white"
                onClick={() => onAccept(lead)}
              >
                <Sparkles className="h-4 w-4" />
                {lead.status === 'new' ? 'Start deal' : 'Continue deal'}
                <ArrowRight className="h-4 w-4 ml-0.5" />
              </Button>

              {/* Snooze */}
              <Button
                variant="outline"
                onClick={() => onSnooze(lead)}
                className="shrink-0"
              >
                <Clock className="h-4 w-4" />
                <span className="hidden sm:inline">Snooze</span>
              </Button>

              {/* Discard */}
              {!showDiscardForm && (
                <Button
                  variant="outline"
                  onClick={() => setShowDiscardForm(true)}
                  className="shrink-0 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                >
                  <Ban className="h-4 w-4" />
                  <span className="hidden sm:inline">Discard</span>
                </Button>
              )}
            </div>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
